import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { pmGet, pmPost, type PmProject } from '../api';
import './RaciDefinition.css';
import { SelectField, PROMPT, UNSET } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule } from '../finalizedRuleHelp';

/**
 * RACI Definition.
 *
 * Cloned from `/Users/glucic/src/ai_readiness_template/views/roadmap-execution/raci.ejs`.
 *
 * WHAT IT DOES, in the source's words: set the decision rights for delivery. For every
 * in-scope work item you assign R / A / C / I across the project roles, so everyone knows
 * who does the work, who owns the outcome, and who is consulted or informed.
 *
 * ONE MATRIX, NOT PER-PHASE PANELS. Every in-scope work item shows at once, grouped under
 * coloured phase bands, with the role header pinned and the work-item column frozen. That
 * is the source's central design decision and the CSS carries it — see the z-index note in
 * `RaciDefinition.css`.
 *
 * DEFAULTS COME FROM THE SERVER, not from here: `/raci/data` falls back to
 * `RACI_DEFAULT_BY_KEY` for any item with no stored matrix, so a fresh project opens
 * pre-filled for a typical delivery team and every cell is still editable.
 *
 * REMOVING A BUILT-IN ROLE HIDES IT FOR THE PROJECT and persists — the server keeps
 * `project_raci_hidden_roles` — which is why removal is not merely a client-side splice.
 *
 * TWO BROWSER DIALOGS ARE KEPT. The source calls `prompt()` to name a new role and
 * `confirm()` before removing one, and both are reproduced rather than replaced with a
 * modal: swapping them would be a redesign, and this is a port.
 *
 * `alert()` BECOMES AN INLINE LINE, carrying the same text. Not a judgement call — it is
 * what the already-ported screens in this module do (`StreamDefinition`, `ResourceTracker`),
 * and CLAUDE.md §1.4 says conform to the convention that already exists rather than
 * introduce a second one.
 */

interface RaciItem {
  id: number;
  opportunity_id: string | null;
  phase: string;
  name: string;
  is_custom: boolean;
  raci: Record<string, string>;
}

interface RaciRole {
  code: string;
  label: string;
  custom: boolean;
}

interface RaciPhase {
  key: string;
  label: string;
  mode: string;
}

interface RaciOpportunity {
  opportunity_id: string;
  name: string;
}

interface RaciData {
  success: boolean;
  error?: string;
  project: PmProject;
  roles: RaciRole[];
  phases: RaciPhase[];
  items: RaciItem[];
  opportunities: RaciOpportunity[];
}

/* The source's own constants, verbatim. */
const PHASE_LABEL: Record<string, string> = {
  Assessment_Planning: 'Assessment & Planning',
  Design_Architecture: 'Design & Architecture',
  Development: 'Development',
  Testing_Validation: 'Testing & Validation',
  GoLive_Optimization: 'Go-Live & Optimization',
  Agile_Delivery: 'Agile Delivery',
};
const PHASE_COLORS: Record<string, string> = {
  Assessment_Planning: '#90caf9',
  Design_Architecture: '#ce93d8',
  Development: '#a5d6a7',
  Testing_Validation: '#ffcc80',
  GoLive_Optimization: '#ef9a9a',
  Agile_Delivery: '#80cbc4',
};
const PHASE_TEXT_COLORS: Record<string, string> = {
  Assessment_Planning: '#1e88e5',
  Design_Architecture: '#8e24aa',
  Development: '#2e7d32',
  Testing_Validation: '#ef6c00',
  GoLive_Optimization: '#d32f2f',
  Agile_Delivery: '#00897b',
};
const RACI_OPTS = ['', 'R', 'A', 'C', 'I', 'A/R'];

/** `cellStyle(v)` — the code's own colour pair. */
function cellStyle(v: string): { background: string; color: string } {
  if (v === 'A/R') return { background: '#fff3e0', color: '#e65100' };
  if (v === 'A') return { background: '#ffebee', color: '#c62828' };
  if (v === 'R') return { background: '#e3f2fd', color: '#1565c0' };
  if (v === 'C') return { background: '#f1f8e9', color: '#558b2f' };
  if (v === 'I') return { background: '#f5f5f5', color: '#9e9e9e' };
  return { background: '#fff', color: '#cfd8dc' };
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/raci.ejs`,
 * its `raciHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const RACI_HELP: HelpContent = {
    title: 'RACI Definition',
    icon: '👥',
    purpose: "Set the <strong>decision rights</strong> for delivery. For every in-scope work item you assign <strong>R / A / C / I</strong> across the standard project roles, so everyone knows who does the work, who owns the outcome, and who is consulted or informed.",
    doHere: [
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong> &mdash; it appears here as a single <strong>Delivery</strong> row covering its whole window, and is fully RACI-able just like any other initiative.",
      "Select a <strong>Project</strong>; the <strong>whole RACI loads on one screen</strong> — every in-scope work item in a single matrix, grouped into colored <strong>phase bands</strong>, with <strong>sensible defaults pre-filled</strong>. Use the <strong>Opportunity</strong> pull-down to view the <strong>entire project</strong> or narrow to a <strong>single opportunity</strong>.",
      "As you scroll, the <strong>role header stays pinned</strong> and the <strong>work item column stays frozen</strong> so you never lose context.",
      "Edit each cell &mdash; set the role's involvement (R, A, C, I or blank) per work item.",
      "Tailor the <strong>role columns</strong> to your team: <strong>Add Role</strong> for a custom one, or <strong>Remove Role</strong> to reveal a ✕ on every column and delete any role (built-in or custom) &mdash; removals stick. Then <strong>Save</strong> so the matrix is locked in before execution.",
      /* NOT IN THE SOURCE. These two controls are work commissioned on this platform
         (2026-09-29), so the source's help does not describe them — and a help panel that
         omitted half the toolbar would be wrong. Logged in docs/DEVIATIONS.md. */
      "If <strong>no RACI has been generated yet</strong>, press <strong>Create RACI</strong> to build one here — it writes the same defaults the AI would, filling only what is blank and leaving anything you have already set alone.",
      "<strong>Add Work Item</strong> puts a row into the matrix: choose its <strong>phase</strong>, whether it is <strong>Global</strong> or belongs to one opportunity, and whether it is a <strong>Deliverable</strong> or an <strong>Activity</strong>. Name it after a standard deliverable and it arrives with the default matrix already filled. Only rows added here carry a ✕ &mdash; a catalogue row belongs to <strong>Work Item Definition</strong> and is removed there."
    ],
    fits: 'Work Item Assignment &rarr; <strong>RACI Definition</strong> &rarr; Budget &amp; Resource Plan &rarr; Execution Control Center',
    firstTime: "Pick a project, review the pre-filled matrix and adjust any R/A/C/I cells that differ for your team, then save.",
    sections: [
      {
        label: 'The RACI codes',
        type: 'defs',
        items: [
          { term: 'R', color: '#1976d2', def: "<strong>Responsible</strong> &mdash; does the work to complete the work item." },
          { term: 'A', color: '#c62828', def: "<strong>Accountable</strong> &mdash; ultimately answerable; approves the work item. Exactly one per work item." },
          { term: 'C', color: '#00838f', def: "<strong>Consulted</strong> &mdash; provides input before the work is done (two-way)." },
          { term: 'I', color: '#6a1b9a', def: "<strong>Informed</strong> &mdash; kept up to date on progress or completion (one-way)." }
        ],
        note: "Defaults reflect a typical SAP/AI delivery team (PM, BA, Tech Lead, Functional/Technical Consultants, etc.) &mdash; override any cell to fit yours."
      },
      {
        label: 'The single-screen matrix',
        type: 'defs',
        items: [
          { term: 'One screen', color: '#1976d2', def: "All in-scope work items show at once in a single matrix &mdash; no per-phase expanding. The <strong>role header stays pinned</strong> at the top and the <strong>work item column is frozen</strong> as you scroll." },
          { term: 'Phase bands', color: '#00838f', def: "Work Items are grouped under colored bands by <strong>phase</strong> (Waterfall) or <strong>MVP wave</strong> (Agile), so you can see how ownership is allocated across the delivery." },
          { term: 'Opportunity filter', color: '#6a1b9a', def: "The <strong>Opportunity</strong> pull-down (next to the project) shows the RACI for the <strong>entire project</strong> or narrows it to a <strong>single opportunity</strong>." },
          { term: 'Roles', color: '#2e7d32', def: "<strong>Add Role</strong> adds a custom column; <strong>Remove Role</strong> turns on a ✕ to delete any column. Removing a built-in role hides it for the whole project and persists." }
        ]
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' },
      { label: 'Next: Budget Plan →', href: '/project_management/program-management/budget-resources/budget-plan' }
    ]
  };

export function RaciDefinition() {
  const [projects, setProjects] = useState<PmProject[]>([]);
  const [projectId, setProjectId] = useState('');
  const [project, setProject] = useState<PmProject | null>(null);
  const [items, setItems] = useState<RaciItem[]>([]);
  const [roles, setRoles] = useState<RaciRole[]>([]);
  const [phaseList, setPhaseList] = useState<RaciPhase[]>([]);
  const [opportunities, setOpportunities] = useState<RaciOpportunity[]>([]);
  const [selectedOpp, setSelectedOpp] = useState(UNSET);
  const [removeMode, setRemoveMode] = useState(false);
  /*
    ADDING A WORK ITEM — the matrix's OTHER dimension (owner-directed 2026-09-29). Roles could
    already be added and removed here; rows could not, so a project whose work items the AI
    never produced opened an empty matrix with nowhere to go. The form is inline rather than a
    `prompt()` chain because it asks for three things, and two of them are choices.
  */
  const [addOpen, setAddOpen] = useState(false);
  const [newPhase, setNewPhase] = useState('');
  const [newScope, setNewScope] = useState('');
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('Deliverable');
  /*
    The source's `alert('RACI saved.')` / `alert('Error: …')`. A blocking alert in an SPA is
    worse than in the EJS page — it freezes the whole shell — so the SAME text lands in a
    line under the toolbar instead. The wording is untouched.
  */
  const [notice, setNotice] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const d = await pmGet<{ projects: PmProject[] }>('/deliverables/projects');
        setProjects(d.projects ?? []);
      } catch {
        /* the source logs and leaves the picker empty */
      }
    })();
  }, []);

  const resetView = () => {
    setProject(null);
    setItems([]);
    setPhaseList([]);
    setOpportunities([]);
    setSelectedOpp('__all__');
    setRemoveMode(false);
  };

  const onSelectProject = useCallback(async (id: string) => {
    if (!id) {
      resetView();
      return;
    }
    try {
      const d = await pmGet<RaciData>(
        `/raci/data?delivery_project_id=${encodeURIComponent(id)}`,
      );
      if (!d.success) {
        setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
        return;
      }
      setProject(d.project);
      setPhaseList(d.phases ?? []);
      setRoles(d.roles ?? []);
      setOpportunities(d.opportunities ?? []);
      setItems((d.items ?? []).map((it) => ({ ...it, raci: it.raci || {} })));
      setSelectedOpp('__all__');
      setRemoveMode(false);
      setNotice(null);
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  }, []);

  /** `visibleItems()` — '__all__' is the entire project. */
  const visible = useMemo(
    () =>
      /* UNSET reads as "entire project", exactly as '__all__' did. */
      selectedOpp === '__all__' || selectedOpp === UNSET
        ? items
        : items.filter((it) => String(it.opportunity_id) === String(selectedOpp)),
    [items, selectedOpp],
  );

  /** `activePhases()` — only phases that carry an in-scope item in the current scope. */
  const activePhases = useMemo(
    () => phaseList.filter((ph) => visible.some((it) => it.phase === ph.key)),
    [phaseList, visible],
  );

  const setRaci = (id: number, role: string, val: string) => {
    setItems((cur) =>
      cur.map((it) => {
        if (it.id !== id) return it;
        const raci = { ...it.raci };
        if (val) raci[role] = val;
        else delete raci[role];
        return { ...it, raci };
      }),
    );
  };

  const addRole = async () => {
    if (!project) return;
    const label = (window.prompt('New role name (e.g., "Quality Lead"):') || '').trim();
    if (!label) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string; role?: RaciRole }>(
        '/raci/role/add',
        { delivery_project_id: project.delivery_project_id, label },
      );
      if (d.success && d.role) setRoles((cur) => [...cur, d.role!]);
      else setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  const removeRole = async (code: string) => {
    if (!project) return;
    if (!window.confirm('Remove this role column? Its RACI values will be cleared.')) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/raci/role/remove', {
        delivery_project_id: project.delivery_project_id,
        code,
      });
      if (d.success) {
        setRoles((cur) => cur.filter((x) => x.code !== code));
        setItems((cur) =>
          cur.map((it) => {
            const raci = { ...it.raci };
            delete raci[code];
            return { ...it, raci };
          }),
        );
      } else {
        setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
      }
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  /* SAVES EVERY ITEM, not just the visible ones — the source posts `currentItems`, so
     narrowing to one opportunity never drops the rest of the project's matrix. */
  const saveRaci = async () => {
    if (!project) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/raci/save', {
        delivery_project_id: project.delivery_project_id,
        items: items.map((it) => ({ id: it.id, raci: it.raci })),
      });
      setNotice(
        d.success
          ? { kind: 'ok', text: 'RACI saved.' }
          : { kind: 'err', text: `Error: ${d.error || 'failed'}` },
      );
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  /**
   * CREATE the RACI, rather than only edit it (owner-directed 2026-09-29).
   *
   * The server writes the same default matrix `/raci/data` already displays, so what lands
   * in the project is the layout the API generates. Items that already carry a decided RACI
   * are left alone — the count says how many — and `overwrite` is not offered from here,
   * because replacing somebody's decisions should be a deliberate, separate act.
   */
  const generateRaci = async () => {
    if (!project) return;
    setNotice(null);
    try {
      const d = await pmPost<{
        success: boolean; error?: string; created?: number; kept?: number; no_default?: number;
      }>('/raci/generate', { delivery_project_id: project.delivery_project_id });
      if (!d.success) {
        setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
        return;
      }
      await onSelectProject(project.delivery_project_id);
      const bits = [`RACI created for ${d.created ?? 0} work item${d.created === 1 ? '' : 's'}`];
      if (d.kept) bits.push(`${d.kept} already had one and were left as they are`);
      if (d.no_default) bits.push(`${d.no_default} have no catalogue default and stay blank`);
      setNotice({ kind: 'ok', text: `${bits.join(' · ')}.` });
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  const addItem = async () => {
    if (!project) return;
    const name = newName.trim();
    const phase = newPhase || phaseList[0]?.key || '';
    if (!name || !phase) {
      setNotice({ kind: 'err', text: 'A work item needs a phase and a name.' });
      return;
    }
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/raci/item/add', {
        delivery_project_id: project.delivery_project_id,
        phase,
        name,
        work_item_type: newType,
        opportunity_id: newScope || null,
      });
      if (!d.success) {
        setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
        return;
      }
      setNewName('');
      setAddOpen(false);
      await onSelectProject(project.delivery_project_id);
      setNotice({ kind: 'ok', text: `Added “${name}”.` });
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  /* Only a row added here can be taken away here — a catalogue item belongs to Work Item
     Definition, and removing it from this screen would delete work scoped there. */
  const removeItem = async (it: RaciItem) => {
    if (!project || !it.is_custom) return;
    if (!window.confirm(`Remove “${it.name}” from this project?`)) return;
    try {
      const d = await pmPost<{ success: boolean; error?: string }>('/raci/item/remove', { id: it.id });
      if (!d.success) {
        setNotice({ kind: 'err', text: `Error: ${d.error || 'failed'}` });
        return;
      }
      await onSelectProject(project.delivery_project_id);
      setNotice({ kind: 'ok', text: `Removed “${it.name}”.` });
    } catch {
      setNotice({ kind: 'err', text: 'Request failed' });
    }
  };

  const loaded = !!project;

  return (
    <div className="pm-raci">
      <HelpPanel help={withFinalizedRule(RACI_HELP)} />
      <div className="rc-head">
        <div className="rc-titlerow">
          <i className="material-icons rc-titleicon">groups</i>
          <div>
            <h1 className="page-header__title">RACI Definition</h1>
            <p className="rc-sub">
              Assign Responsible, Accountable, Consulted, Informed per in-scope work item, by
              phase. Defaults are pre-filled and editable.
            </p>
          </div>
        </div>

        <div className="dl-toolbar">
          <SelectField
            label="Project"
            width="lg"
            id="raci-project"
            value={projectId}
            onChange={(v) => {
              setProjectId(v);
              void onSelectProject(v);
            }}
            placeholder={PROMPT.project}
            options={projects.map((p) => ({
              value: p.delivery_project_id,
              id: p.delivery_project_id,
                description: p.description,
            }))}
          />

          {opportunities.length ? (
            <SelectField
              label="Opportunity"
              width="lg"
              id="raci-opp"
              value={selectedOpp}
              onChange={(v) => setSelectedOpp(v || '__all__')}
              placeholder={PROMPT.opportunity}
              options={[
                { value: '__all__', label: 'All opportunities (entire project)' },
                ...opportunities.map((o) => ({
                  value: o.opportunity_id,
                  // No slice(0, 42): the field cuts with an ellipsis and the open panel
                  // shows the name in full, so nothing has to be pre-truncated here.
                  id: o.opportunity_id,
                  description: o.name && o.name !== o.opportunity_id ? o.name : undefined,
                })),
              ]}
            />
          ) : null}

          {loaded ? (
            <div className="rc-actions">
              <button type="button" className="rc-rolebtn" onClick={() => void addRole()}>
                <i className="material-icons">add</i> Add Role
              </button>
              <button
                type="button"
                className={`rc-rolebtn${addOpen ? ' on' : ''}`}
                title="Add a work item row to this matrix"
                onClick={() => {
                  setAddOpen((o) => !o);
                  if (!newPhase) setNewPhase(phaseList[0]?.key ?? '');
                }}
              >
                <i className="material-icons">playlist_add</i> Add Work Item
              </button>
              <button
                type="button"
                className={`rc-rolebtn rm${removeMode ? ' on' : ''}`}
                title="Show a ✕ on each role column, then click one to remove that role"
                onClick={() => setRemoveMode((m) => !m)}
              >
                <i className="material-icons">remove</i> Remove Role
              </button>
              <button
                type="button"
                className="rc-rolebtn"
                title="Fill every work item that has no RACI yet with the standard matrix for its catalogue item"
                onClick={() => void generateRaci()}
              >
                <i className="material-icons">auto_awesome_motion</i> Create RACI
              </button>
              <button type="button" className="btn-primary" onClick={() => void saveRaci()}>
                Save RACI
              </button>
            </div>
          ) : null}
        </div>

        {loaded && addOpen ? (
          <div className="rc-additem">
            <select className="rc-additem-f" value={newPhase} onChange={(e) => setNewPhase(e.target.value)}>
              {phaseList.map((ph) => (
                <option key={ph.key} value={ph.key}>
                  {PHASE_LABEL[ph.key] || ph.label}
                </option>
              ))}
            </select>
            <select className="rc-additem-f" value={newScope} onChange={(e) => setNewScope(e.target.value)}>
              <option value="">Global (whole project)</option>
              {opportunities.map((o) => (
                <option key={o.opportunity_id} value={o.opportunity_id}>
                  {o.opportunity_id}
                  {o.name && o.name !== o.opportunity_id ? ` — ${o.name}` : ''}
                </option>
              ))}
            </select>
            <select className="rc-additem-f" value={newType} onChange={(e) => setNewType(e.target.value)}>
              <option value="Deliverable">Deliverable</option>
              <option value="Activity">Activity</option>
            </select>
            <input
              className="rc-additem-f rc-additem-name"
              placeholder="Work item name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void addItem();
              }}
            />
            <button type="button" className="btn-primary" onClick={() => void addItem()}>
              Add
            </button>
            <button type="button" className="rc-rolebtn" onClick={() => setAddOpen(false)}>
              Cancel
            </button>
          </div>
        ) : null}

        {loaded ? (
          <div className="rc-legendbar">
            <div className="legend">
              <span className="lg-chip">
                <span className="lg-key" style={{ background: '#ffebee', color: '#c62828' }}>
                  A
                </span>{' '}
                Accountable
              </span>
              <span className="lg-chip">
                <span className="lg-key" style={{ background: '#e3f2fd', color: '#1565c0' }}>
                  R
                </span>{' '}
                Responsible
              </span>
              <span className="lg-chip">
                <span className="lg-key" style={{ background: '#f1f8e9', color: '#558b2f' }}>
                  C
                </span>{' '}
                Consulted
              </span>
              <span className="lg-chip">
                <span className="lg-key" style={{ background: '#f5f5f5', color: '#9e9e9e' }}>
                  I
                </span>{' '}
                Informed
              </span>
              <span className="lg-chip">
                <span className="lg-key" style={{ background: '#fff3e0', color: '#e65100' }}>
                  A/R
                </span>{' '}
                Accountable &amp; Responsible
              </span>
            </div>
            {notice ? (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: notice.kind === 'ok' ? '#2e7d32' : '#c62828',
                }}
              >
                {notice.text}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="raci-scroll">
        {visible.length ? (
          <table className="raci-matrix">
            <thead>
              <tr>
                <th className="delh">Work Item</th>
                {roles.map((r) => (
                  <th key={r.code} className="role-h">
                    {r.label}
                    {removeMode ? (
                      <span
                        className="role-rm"
                        title="Remove this role"
                        onClick={(e) => {
                          e.stopPropagation();
                          void removeRole(r.code);
                        }}
                      >
                        ✕
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {activePhases.map((ph) => {
                const rows = visible.filter((it) => it.phase === ph.key);
                const color = PHASE_COLORS[ph.key] || '#b0bec5';
                const textColor = PHASE_TEXT_COLORS[ph.key] || '#37474f';
                return (
                  <Fragment key={ph.key}>
                    <tr className="phase-band">
                      <td
                        colSpan={1 + roles.length}
                        style={
                          {
                            background: `${color}20`,
                            '--pc': color,
                          } as React.CSSProperties
                        }
                      >
                        <div className="pb" style={{ color: textColor }}>
                          <span className="lbl">
                            <span className="dl-dot" style={{ background: color }} />
                            {ph.mode === 'agile' ? '🔁 ' : ''}
                            {PHASE_LABEL[ph.key] || ph.label}
                            <span className="band-n">{rows.length}</span>
                          </span>
                        </div>
                      </td>
                    </tr>
                    {rows.map((it) => (
                      <tr key={it.id}>
                        <td className="raci-name">
                          {it.name}
                          {it.is_custom ? (
                            <button
                              type="button"
                              className="rc-rowx"
                              title="Remove this work item — it was added on this screen"
                              onClick={() => void removeItem(it)}
                            >
                              ✕
                            </button>
                          ) : null}
                        </td>
                        {roles.map((r) => {
                          const v = it.raci[r.code] || '';
                          return (
                            <td key={r.code} className="role-c">
                              <select
                                className="raci-cell"
                                style={cellStyle(v)}
                                value={v}
                                onChange={(e) => setRaci(it.id, r.code, e.target.value)}
                                aria-label={`${r.label} for ${it.name}`}
                              >
                                {RACI_OPTS.map((o) => (
                                  <option key={o || 'blank'} value={o}>
                                    {o || '·'}
                                  </option>
                                ))}
                              </select>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="rc-empty">
            <i className="material-icons ic">groups</i>
            {loaded ? (
              <>
                <h3>No In-Scope Work Items</h3>
                <p>
                  Mark work items in scope on the <strong>Work Item Definition</strong> screen
                  first.
                </p>
              </>
            ) : (
              <>
                <h3>Select a Project</h3>
                <p>
                  Pick a project above. The RACI covers the work items marked in scope on the
                  Work Item Definition screen.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
